// @flow
import React, { useMemo, useContext, useState, useEffect, Fragment } from 'react';
import isEmpty from 'ramda/es/isEmpty';
import prop from 'ramda/es/prop';
import groupWith from 'ramda/es/groupWith';
import { useTranslation } from 'react-i18next';
import VisibilitySensor from 'react-visibility-sensor';

import RowLayout, { Space } from 'app/common/layouts/RowLayout';
import { HeaderLvl3 } from 'app/common/layouts/markup';
import { commaJoin } from 'app/common/lib/address';
import EmptyWarn from 'app/common/layouts/EmptyWarn';
import { Price } from 'app/common/layouts/commonShop';
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue';
import { DotsLoader } from 'app/common/components/Loader';
import isProduction from 'app/common/lib/isProduction';

import {
  ECartItem,
  ECartItemThumb,
  ECartItemTitle,
  ECartItemContent,
  PerItem,
} from 'app/common/layouts/ecart';

// $FlowFixMe
import * as paymentStatuses from 'app/common/actions/shop/order/paymentStatuses';
// $FlowFixMe
import * as shippingStatuses from 'app/common/actions/shop/order/shippingStatuses';
// $FlowFixMe
import * as paymentTypes from 'app/common/actions/shop/order/paymentTypes';

import Summary from '../../Summary';
import PaymentStatus from '../../PaymentStatus';
import ShippingStatus from '../../ShippingStatus';
import OrderFooter from './OrderFooter';
import { OrderContext } from '../../OrderData';
import DateFrom from '../../DateFrom';
import OrderDetailsButton from './OrderDetailsButton';
import OrderCancelButton from './OrderCancelButton';
import OrderRepeatButton from './OrderRepeatButton';


import styles from './Order.module.scss';;


const emptyArr = [];
const emptyObj = {};

export type OrderProps = {
  id: string,
  number: string,
  date?: string,
  timezone: string,
  loading: boolean,
  noIntroSpace?: boolean,
  hideButtons?: boolean,
  reloadOscilator?: boolean,
  onRedirectToCart?: () => void,
}

const Order = ({
  id,
  number,
  date,
  timezone,
  loading: loadingExternal,
  noIntroSpace,
  hideButtons,
  reloadOscilator,
  onRedirectToCart,
}: OrderProps) => {
  const { t } = useTranslation('shop');

  const {
    loading: { order: loadingData },
    content: { order },
    request,
    update,
  } = useContext(OrderContext);

  const {
    payment_method_type: paymentType,
    payment_status: currentPaymentStatus,
    shipping_status: currentShippingStatus,
    amount_discount: amountDiscount,
    order_items: orderItems = emptyArr,
    postal_code: postalCode,
    state_name: stateName,
    delivery_point: deliveryPoint = emptyObj,
    city,
    street,
    comment,
    total,
  } = order;

  const address = useMemo(() => commaJoin(
    postalCode,
    stateName,
    city,
    street,
    prop('name', deliveryPoint),
  ), [
    postalCode,
    stateName,
    city,
    street,
    deliveryPoint,
  ]);

  const [visibility, setVisibility] = useState(false);
  const [requested, setRequested] = useState(false);
  useEffect(() => {
    if (!requested && visibility) {
      request.order();
      setRequested(true);
    }
  }, [visibility]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    request.order();
  }, [reloadOscilator]); // eslint-disable-line react-hooks/exhaustive-deps

  const loading = loadingData || loadingExternal;

  const nothing = isEmpty(orderItems) && !loading;

  const orderItemsGrouped = useMemo(() => groupWith(({ variant_id: vId1 }, { variant_id: vId2 }) => vId1 === vId2, orderItems), [orderItems]);

  return (
    <VisibilitySensor onChange={setVisibility} partialVisibility delayedCall>
      <Fragment>
        {!noIntroSpace && <Space />}
        <RowLayout single={!noIntroSpace} last={noIntroSpace} flex>

          <div className={styles.content}>
            <HeaderLvl3 className={styles.header}>
              {t('order_in_list_header', { number })}
              <DateFrom date={date || ''} timezone={timezone} />
              {loading && <DotsLoader simple />}
              <PaymentStatus status={currentPaymentStatus} />
              {!isProduction()
                && id
                && <span style={{ fontSize: 10, float: 'right' }}>
                  {'id:'}
&nbsp;
                  {id}
                </span>}
            </HeaderLvl3>

            <ShippingStatus status={currentShippingStatus} />

            {nothing
              && <EmptyWarn>{'Не удалось получить товары в заказе'}</EmptyWarn>}

            {!nothing
              && orderItemsGrouped.map(([{ id: itemId, image, name, price }, ...othersSameVariant]) => {
                const quantity = othersSameVariant.length + 1;
                const itemPrice = `${toFixedValueCurrency(price)}/шт.`;
                const totalPrice = toFixedValueCurrency(price * quantity);
                const quantityText = `${quantity} шт.`;

                return (
                  <ECartItem
                    id={itemId}
                    key={itemId}
                    // className={}
                  >
                    {<ECartItemThumb imgSrc={prop('standart', image)} title={name} />}
                    <ECartItemTitle className={styles.nameCol}>
                      {name}
                      {/* <Stock>Есть на складе</Stock> */}
                    </ECartItemTitle>
                    <ECartItemContent>
                      <Price>{quantityText}</Price>
                      <PerItem className={styles.pricePerItem}>
                        {itemPrice}
                      </PerItem>
                    </ECartItemContent>
                    <ECartItemContent className={styles.priceCol}>
                      <Price>{totalPrice}</Price>
                    </ECartItemContent>
                  </ECartItem>
                );
              })}

            <OrderFooter
              address={address}
              comment={comment}
              t={t}
            />

          </div>
          <Summary
            className={styles.summary}
            itemsNumber={orderItems.length}
            sum={total}
            amountDiscount={amountDiscount}
            total={total}
            hideButtons={hideButtons}
          >

            {/* КНОПКА ОПЛАТЫ ЗАКАЗА */}
            {/* только если PAYMENT_STATUS_UNPAID и метод оплаты карта и заказ уже не отменен */}
            {currentPaymentStatus === paymentStatuses.PAYMENT_STATUS_UNPAID
              && paymentType === paymentTypes.PAYMENT_TYPE_CARD
              && currentShippingStatus !== shippingStatuses.SHIPPING_STATUS_CANCELED_BY_CUSTOMER
              && <OrderDetailsButton
                id={id}
                number={number}
                className={styles.button}
              />}

            {/* КНОПКА ОТМЕНЫ ЗАКАЗА */}
            {/* только если PAYMENT_STATUS_UNPAID и заказ не отменен уже */}
            {currentPaymentStatus === paymentStatuses.PAYMENT_STATUS_UNPAID
              && currentShippingStatus !== shippingStatuses.SHIPPING_STATUS_CANCELED_BY_CUSTOMER
              && <OrderCancelButton
                id={id}
                number={number}
                className={styles.button}
                onUpdateOrder={update.order}
              />}

            {/* КНОПКА ПОВТОРЕНИЯ ЗАКАЗА */}
            {/* если есть товары */}
            {((!nothing
              && currentShippingStatus === shippingStatuses.SHIPPING_STATUS_CANCELED_BY_CUSTOMER)

              || (!nothing
                && currentShippingStatus !== shippingStatuses.SHIPPING_STATUS_WAITING
                && currentShippingStatus !== shippingStatuses.SHIPPING_STATUS_READY_FOR_ISSUE
                && currentPaymentStatus !== paymentStatuses.PAYMENT_STATUS_UNPAID))

              && <OrderRepeatButton
                className={styles.button}
                orderItems={orderItems}
                onRedirectToCart={onRedirectToCart}
              />}

          </Summary>
        </RowLayout>
      </Fragment>
    </VisibilitySensor>
  );
};

export default Order;
